// Role-Based Access Control (RBAC) helper
import { ExtendedUser, useAuth } from '@/contexts/AuthContext';
import { canonicalizeRoles, pickHighestPriorityRole } from '@/lib/roles';

export type Permission =
  | 'view_bots'
  | 'edit_bots'
  | 'start_stop_bots'
  | 'view_strategies'
  | 'edit_strategies'
  | 'view_venues'
  | 'connect_venues'
  | 'view_reports'
  | 'export_reports'
  | 'view_compliance'
  | 'export_compliance'
  | 'approve_changes'
  | 'view_monitoring'
  | 'view_admin'
  | 'edit_settings'
  | 'manage_kpis'
  | 'strategy_admin';

// Permission matrix by role
const rolePermissions: Record<string, Permission[]> = {
  org_owner: [
    'view_bots', 'edit_bots', 'start_stop_bots',
    'view_strategies', 'edit_strategies',
    'view_venues', 'connect_venues',
    'view_reports', 'export_reports',
    'view_compliance', 'export_compliance',
    'approve_changes',
    'view_monitoring',
    'view_admin',
    'edit_settings',
    'manage_kpis',
    'strategy_admin'
  ],
  rugira_platform_admin: [
    'view_bots', 'edit_bots', 'start_stop_bots',
    'view_strategies', 'edit_strategies',
    'view_venues', 'connect_venues',
    'view_reports', 'export_reports',
    'view_compliance', 'export_compliance',
    'approve_changes',
    'view_monitoring',
    'view_admin',
    'edit_settings',
    'manage_kpis',
    'strategy_admin'
  ],
  rugira_compliance_admin: [
    'view_reports', 'export_reports',
    'view_compliance', 'export_compliance',
    'approve_changes',
    'view_monitoring',
    'view_admin',
    'strategy_admin'
  ],
  trading_operator: [
    'view_bots', 'edit_bots', 'start_stop_bots',
    'view_strategies', 'edit_strategies',
    'view_venues'
  ],
  analyst: [
    'view_strategies',
    'view_reports',
    'manage_kpis'
  ],
  auditor: [
    'view_reports', 'export_reports',
    'view_compliance', 'export_compliance'
  ],
  rugira_support: [
    'view_admin',
    'approve_changes'
  ]
};

export class RBACHelper {
  private user: ExtendedUser | null;
  private currentTenant: string | null;

  constructor(user: ExtendedUser | null, currentTenant: string | null) {
    this.user = user;
    this.currentTenant = currentTenant;
  }

  private tenantRoles(): string[] {
    if (!this.user) return [];
    if (this.currentTenant && this.user.tenant_roles?.[this.currentTenant]) {
      return canonicalizeRoles(this.user.tenant_roles[this.currentTenant]);
    }
    if (Array.isArray(this.user.roles)) {
      return canonicalizeRoles(this.user.roles);
    }
    return [];
  }

  private getCurrentRole(): string | null {
    const roles = this.tenantRoles();
    if (!roles.length) return null;
    return pickHighestPriorityRole(roles);
  }

  can(permission: Permission): boolean {
    const role = this.getCurrentRole();
    if (!role) return false;
    const permissions = rolePermissions[role] || [];
    return permissions.includes(permission);
  }

  canView(resource: string): boolean {
    return this.can(`view_${resource}` as Permission);
  }

  canEdit(resource: string): boolean {
    return this.can(`edit_${resource}` as Permission);
  }

  canExport(resource: string): boolean {
    return this.can(`export_${resource}` as Permission);
  }

  getDisabledReason(permission: Permission): string {
    const role = this.getCurrentRole();
    if (!role) return 'Authentication required';

    if (permission.includes('export')) {
      if (role === 'rugira_support') return 'Support users cannot export data';
      if (role === 'analyst') return 'Requires Org Owner approval';
      if (role === 'trading_operator') return 'Requires Org Owner approval';
    }

    if (permission.includes('approve')) {
      return 'Requires Org Owner or Compliance Admin role';
    }

    if (permission === 'start_stop_bots') {
      if (role === 'auditor' || role === 'analyst' || role === 'rugira_support') {
        return 'Requires Trading Operator role or higher';
      }
    }

    if (permission === 'connect_venues') {
      if (!['org_owner', 'trading_operator', 'rugira_platform_admin'].includes(role)) {
        return 'Requires Admin or Trading Operator role';
      }
    }

    return 'Insufficient permissions';
  }

  hasRole(role: string): boolean {
    const canonical = role ? role.trim().toLowerCase() : '';
    if (!canonical) return false;
    return this.tenantRoles().includes(canonical);
  }

  hasAnyRole(roles: Iterable<string>): boolean {
    const roleList = Array.isArray(roles) ? roles : Array.from(roles);
    return roleList.some((role) => this.hasRole(role));
  }

  listRoles(): string[] {
    return this.tenantRoles();
  }

  primaryRole(): string | null {
    return this.getCurrentRole();
  }
}

export function useRBAC(user?: ExtendedUser | null, currentTenant?: string | null): RBACHelper {
  const auth = useAuth();
  const resolvedUser = user ?? auth?.user ?? null;
  const resolvedTenant = currentTenant ?? auth?.currentTenant ?? auth?.user?.current_tenant ?? null;
  return new RBACHelper(resolvedUser, resolvedTenant);
}
